import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Centrale } from '@urbasolar_dev/urbaconnect-types'
import { ChangeEvent, createRef, FunctionComponent, useEffect, useState } from 'react'
import { useTheme } from 'styled-components'
import { useNavigate } from 'react-router'

import { useStore } from '../../../application/store/store'
import { useGetAllUserPlantsUseCase } from '../../../application/useCases/usePlantSelect'
import { useCustomTranslation } from '../../../infrastructure/translation/useCustomTranslation'
import {
  CenterWrapper,
  IconWrapper,
  PlantElementWrapper,
  PlantFlexDiv,
  PlantSelectWrapper,
  SearchBarWrapper,
  SearchInput,
} from './plantSelect.styles'
import { DASHBOARD_URL, MY_PLANTS_URL } from '../../../constants/navigation.constants'

interface IPlantEltProps {
  plant: Centrale
  onClick: () => void
  isMobile: boolean
}

const PlantElement = (props: IPlantEltProps) => {
  const { plant, onClick, isMobile } = props
  return (
    <PlantElementWrapper onClick={onClick}>
      {!isMobile && <PlantFlexDiv>{plant.code_centrale}</PlantFlexDiv>}
      {!isMobile && <PlantFlexDiv>{plant.code_projet}</PlantFlexDiv>}
      <PlantFlexDiv>{plant.nom}</PlantFlexDiv>
    </PlantElementWrapper>
  )
}

export const filterByValue = (
  plants: Centrale[] | undefined,
  string: string | undefined
): Centrale[] => {
  if (!plants) return []
  if (!string) return plants
  return plants.filter((plant) =>
    (Object.keys(plant) as Array<keyof Centrale>).some((plantKey) =>
      String(plant[plantKey]).toLowerCase().includes(string.toLowerCase())
    )
  )
}

const PlantSelect: FunctionComponent = () => {
  const inputRef = createRef<HTMLInputElement>()
  const { plants, plantLoading, activeMenu, setPlantSelected, setAllUserPlants } = useStore()
  const { getAllUserPlants } = useGetAllUserPlantsUseCase()
  const [filteredList, setFilteredList] = useState(plants)
  const [inputValue, setInputValue] = useState<string | undefined>(undefined)
  const { t } = useCustomTranslation('common')
  const navigate = useNavigate()

  const { isMobile } = useTheme()

  useEffect(() => {
    const fetchData = async () => {
      if (inputRef.current) inputRef.current.focus() // Focus on search input on mount
      const data = await getAllUserPlants()
      data && setAllUserPlants(data)
    }
    fetchData()
  }, [])

  const handlePlantClick = (plant: Centrale) => {
    setPlantSelected(plant)
    if (activeMenu === MY_PLANTS_URL) {
      navigate(MY_PLANTS_URL)
    } else {
      navigate(DASHBOARD_URL)
    }
  }

  const isPlantListFilled = !inputValue && plants?.length > 0
  const isPlantListEmpty = !inputValue && plants?.length === 0
  const isFilteredListEmpty = inputValue && filteredList?.length === 0
  const isFilteredListFilled = inputValue && filteredList?.length > 0

  return (
    <PlantSelectWrapper>
      <SearchBarWrapper>
        <IconWrapper>
          <FontAwesomeIcon icon="search" />
        </IconWrapper>
        <SearchInput
          type="text"
          name="plantSearchInput"
          placeholder="Rechercher une centrale"
          ref={inputRef}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setInputValue(e.target.value)
            setFilteredList(filterByValue(plants, e.target.value))
          }}
        />
      </SearchBarWrapper>
      {plantLoading && (
        <CenterWrapper>
          <FontAwesomeIcon icon="spinner" spin size="lg" />
          <p>{`${t('collect_your_plants')}...`}</p>
        </CenterWrapper>
      )}

      {!plantLoading &&
        isPlantListFilled &&
        plants.map((plant) => {
          return (
            <PlantElement
              key={plant.code_centrale}
              plant={plant}
              onClick={() => handlePlantClick(plant)}
              isMobile={isMobile}
            />
          )
        })}
      {!plantLoading && isPlantListEmpty && (
        <CenterWrapper>
          <FontAwesomeIcon icon="exclamation-triangle" size="lg" />
          <p>{t('no_plant')}</p>
        </CenterWrapper>
      )}

      {!plantLoading &&
        isFilteredListFilled &&
        filteredList.map((plant) => {
          return (
            <PlantElement
              key={plant.code_centrale}
              plant={plant}
              onClick={() => handlePlantClick(plant)}
              isMobile={isMobile}
            />
          )
        })}
      {!plantLoading && isFilteredListEmpty && (
        <CenterWrapper>
          <FontAwesomeIcon icon="exclamation-triangle" size="lg" />
          <p>{t('no_plant_according_research')}</p>
        </CenterWrapper>
      )}
    </PlantSelectWrapper>
  )
}

export default PlantSelect
